import { SecondaryButton } from "@/components/buttons/SecondaryButton";
import { selectStandardProps } from "@/utils/componentUtils";
import { formatLocaleDateTime } from "@/utils/formatUtils";
import { saveTextFile } from "@/utils/utils";
import { App as AntApp, Col, Modal, Row, Select } from "antd";
import { useState } from "react";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { useTranslation } from "react-i18next";
import { useGetCalcRecordStackTracesLogs } from "../../../../../common/api/queries/calcRecords";
import AntIcon from "../../../../../common/components/icons/AntIcon";
import { ModalSizes } from "../../../../../common/constants";
import { UUID } from "../../../../../typings/global";

interface Props {
  id?: UUID;
  onCancelClick: () => void;
}

export const CalcRecordStackTracesLogsModal = ({ id, onCancelClick }: Props) => {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();

  const [selectedLogIndex, setSelectedLogIndex] = useState<number>(0);

  const { data: record } = useGetCalcRecordStackTracesLogs(id ?? "");
  const stackTraceLog = record?.stackTracesLogs[selectedLogIndex]?.stackTrace;

  const handleLogFileSave = (content: string) => {
    saveTextFile(content, createLogsTitle());
  };

  const handleCopyInfoMessageDisplay = () => {
    message.info(t("common.clipboardCopied"));
  };

  const createLogsTitle = () => {
    return (
      t("calc.records.titles.stackTraceLogs") +
      ` - ${record?.user.name} / ` +
      (record?.createdAt ? formatLocaleDateTime(record?.createdAt) : "")
    );
  };

  return (
    <Modal
      open={!!stackTraceLog}
      title={createLogsTitle()}
      width={ModalSizes.HUGE}
      centered
      onCancel={onCancelClick}
      afterClose={() => setSelectedLogIndex(0)}
      footer={null}
    >
      <Row gutter={16} className="margin-bottom-medium">
        <Select
          {...selectStandardProps}
          style={{ width: "200px" }}
          value={selectedLogIndex}
          options={record?.stackTracesLogs.map((log, index) => ({
            value: index,
            label: log.institution + (log.coverage ? " - " + log.coverage : ""),
          }))}
          onChange={setSelectedLogIndex}
        />
      </Row>

      {stackTraceLog ? (
        <Row gutter={16}>
          <Col span={24}>
            <pre className="modal-code-block">{stackTraceLog}</pre>

            <div className="center-align margin-top-small">
              <span className="margin-right-medium">
                <SecondaryButton icon={<AntIcon type="download" />} onClick={() => handleLogFileSave(stackTraceLog)}>
                  {t("common.download")}
                </SecondaryButton>
              </span>

              <CopyToClipboard text={stackTraceLog} onCopy={handleCopyInfoMessageDisplay}>
                <SecondaryButton icon={<AntIcon type="copy" />}>{t("common.copy")}</SecondaryButton>
              </CopyToClipboard>
            </div>
          </Col>
        </Row>
      ) : undefined}
    </Modal>
  );
};
